import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Department,
  FilterGroupingEnum,
  useDashboardTaskShortQuery,
} from 'graphql-common';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import useSearchParam from '@lib/hooks/useSearchParam';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import { APP_URLS } from 'constants/urls';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useDepartmentsQueryHook from 'utils/fetch/hooks/useDepartmentsQueryHook';
import getDepartmentsTableColumns from 'routes/Departments/utils/getDepartmentsTableColumns';

export default function TaskDepartmentsContainer() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.TasksManagement) ===
    AccessLevelEnum.Read;

  const taskQuery = useDashboardTaskShortQuery({
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only',
  });
  const { loading: taskLoading } = taskQuery;
  const task = _get(taskQuery, ['data', 'data']) || undefined;
  const name = task?.name || '';
  const groupKey = task?.groupKey || '';

  const { collection, loading, firstLoading, error, totalCount } =
    useDepartmentsQueryHook({
      searchQuery,
      paginationParams,
      additionalFilters: groupKey
        ? [
            {
              departmentTaskGroupsGroupKey: {
                grouping: FilterGroupingEnum.And,
                predicate: { eq: groupKey },
              },
            },
          ]
        : undefined,
      skip: !groupKey,
    });

  const onEditTask = () =>
    navigate(
      APP_URLS.dashboard.tasks.edit.getDynamicPath({ pathParts: { id } }),
    );

  const onView = (department: Department) => {
    if (department?.id) {
      navigate(
        APP_URLS.dashboard.departments.view.getDynamicPath({
          pathParts: { id: department.id },
        }),
      );
    }
  };

  const tableColumns = getDepartmentsTableColumns({ onView, t });

  const isEmptyStateVisible = collection.length === 0 && !error && !loading;
  const isEmptyStateByOutOfRangePage = isEmptyStateVisible && !!totalCount;
  let emptyState: React.ReactNode = null;
  if (isEmptyStateByOutOfRangePage) {
    emptyState = <PageOutOfRangeEmptyState totalEntries={totalCount} />;
  } else if (isEmptyStateVisible) {
    emptyState = (
      <ListEmptyState
        title={t('no-task-departments-linked-yet')}
        text={t('no-task-departments-linked-text')}
        actions={
          isViewOnly
            ? []
            : [
                <Button
                  key="edit"
                  buttonText={t('edit-task')}
                  buttonType={ButtonTypes.primaryFilled}
                  leftIcon="edit"
                  onClick={onEditTask}
                  disabled={loading}
                />,
              ]
        }
      />
    );
  }

  const isErrorStateVisible = !!error && !loading;
  const errorState = isErrorStateVisible ? (
    <ErrorPage
      status={getStatusFromError(error)}
      description={getDescriptionFromError(error)}
    />
  ) : null;

  const isContentVisible = !error && !firstLoading && collection?.length;
  const content = isContentVisible ? (
    <>
      {(!!collection?.length || searchQuery) && (
        <Search placeholder={t('departments-search-placeholder')} />
      )}
      <Table
        columns={tableColumns}
        data={collection}
        emptyState={emptyState}
        id="taskDepartments"
        loading={loading}
        totalEntries={totalCount}
      />
    </>
  ) : null;

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({ pathname, t, entity: task })}
      />
      {taskLoading ? (
        <CircleLoader />
      ) : (
        <WhiteBox>
          <DashboardTitleHeader
            title={name}
            actions={
              isViewOnly ? (
                <ViewOnlyChip key="view" />
              ) : (
                <Button
                  key="edit"
                  buttonText={t('edit')}
                  buttonType={ButtonTypes.primaryOutlined}
                  leftIcon="edit"
                  onClick={onEditTask}
                />
              )
            }
          />
          {firstLoading && <CircleLoader />}
          {errorState}
          {emptyState}
          {content}
        </WhiteBox>
      )}
    </>
  );
}
